import React, { useState, useEffect, useRef } from 'react';
import './PhoneInput.css';

export interface CountryCode {
  code: string;   // e.g. "IN"
  dial: string;   // e.g. "+91"
  name: string;   // e.g. "India"
}

const COUNTRY_CODES: CountryCode[] = [
  { code: 'IN', dial: '+91',  name: 'India' },
  { code: 'US', dial: '+1',   name: 'United States' },
  { code: 'GB', dial: '+44',  name: 'United Kingdom' },
  { code: 'AE', dial: '+971', name: 'UAE' },
  { code: 'SG', dial: '+65',  name: 'Singapore' },
  { code: 'AU', dial: '+61',  name: 'Australia' },
  { code: 'CA', dial: '+1',   name: 'Canada' },
  { code: 'DE', dial: '+49',  name: 'Germany' },
  { code: 'FR', dial: '+33',  name: 'France' },
  { code: 'JP', dial: '+81',  name: 'Japan' },
  { code: 'CN', dial: '+86',  name: 'China' },
  { code: 'BR', dial: '+55',  name: 'Brazil' },
  { code: 'ZA', dial: '+27',  name: 'South Africa' },
  { code: 'NG', dial: '+234', name: 'Nigeria' },
  { code: 'NZ', dial: '+64',  name: 'New Zealand' },
];

/** Tries to extract a known dial code from the start of a phone string. */
function parsePhone(value: string): { countryCode: CountryCode; digits: string } {
  const sorted = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (value.startsWith(c.dial)) {
      return { countryCode: c, digits: value.slice(c.dial.length) };
    }
  }
  return { countryCode: COUNTRY_CODES[0], digits: value };
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function PhoneInput({ value, onChange, placeholder = '', className }: PhoneInputProps) {
  const parsed = parsePhone(value || '');
  const [selected, setSelected] = useState<CountryCode>(parsed.countryCode);
  const [digits, setDigits] = useState(parsed.digits);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync when value prop changes externally (e.g. form reset)
  useEffect(() => {
    const p = parsePhone(value || '');
    setSelected(p.countryCode);
    setDigits(p.digits);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (c: CountryCode) => {
    setSelected(c);
    setOpen(false);
    onChange(c.dial + digits);
  };

  const handleDigitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setDigits(raw);
    onChange(selected.dial + raw);
  };

  return (
    <div className={`phone-input-wrapper${className ? ` ${className}` : ''}`} ref={wrapperRef}>
      {/* Dial code trigger */}
      <button
        type="button"
        className="phone-dial-btn"
        onClick={() => setOpen((o) => !o)}
        title={selected.name}
      >
        {selected.dial} <span className="phone-dial-caret">▾</span>
      </button>

      {/* Dropdown list */}
      {open && (
        <div className="phone-dial-dropdown">
          {COUNTRY_CODES.map((c) => (
            <div
              key={c.code}
              className={`phone-dial-option${c.code === selected.code ? ' selected' : ''}`}
              onMouseDown={() => handleSelect(c)}
            >
              <span className="phone-dial-option-code">{c.dial}</span>
              <span className="phone-dial-option-name">{c.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Phone number input */}
      <input
        type="text"
        inputMode="numeric"
        className="phone-digits-input"
        value={digits}
        onChange={handleDigitsChange}
        placeholder={placeholder}
        maxLength={10}
      />
    </div>
  );
}

export default PhoneInput;
